import { ComponentProps, useState } from "react";
import { TextUnderscore } from "../../../shared/ui/text-underscore";
import "./gameplay-layout.scss";
import { AnimatePresence, motion, Variants } from "framer-motion";

export interface GameplayLayoutProps extends ComponentProps<"div"> {
  prizes: Array<JSX.Element> | JSX.Element | string;
  question: string;
  hint?: Array<JSX.Element> | JSX.Element | string;
  children: Array<JSX.Element> | JSX.Element | string;
  timer?: number;
  players?: Array<Array<JSX.Element> | JSX.Element | string>;
  blurQuestion?: boolean;
  extraTime?: number;
}

const questionVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.5,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
    },
  },
};

const answersVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -100,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const hintsVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      delay: 0.3,
    },
  },
};

export function GameplayLayout({
  extraTime = 0,
  prizes,
  question,
  hint,
  children,
  timer = -2,
  players = [],
  blurQuestion = false,
  ...props
}: GameplayLayoutProps) {
  const [timerVariants] = useState<Variants>({
    shake: () => ({
      rotate: [0, 12, -12, 0],
      transition: {
        duration: 0.3,
        ease: "linear",
        times: [0, 0.33, 0.66, 1],
        repeat: Infinity,
        repeatDelay: 5,
        delay: 5,
      },
    }),
    shakeFaster: () => ({
      rotate: [0, 12, -12, 0],
      transition: {
        duration: 0.3,
        ease: "linear",
        times: [0, 0.33, 0.66, 1],
        repeat: Infinity,
        repeatDelay: 0,
        delay: 1,
      },
    }),
    disappear: () => ({
      opacity: 0,
    }),
    appear: () => ({
      opacity: 1,
    }),
    appearExtraTime: () => ({
      opacity: 1,
      scale: 1,
      rotate: -15,
    }),
    disappearExtraTime: () => ({
      opacity: 0,
      scale: 0,
      rotate: -15,
    }),
  });

  return (
    <div className="gameplay-container" {...props}>
      <div className="gameplay-content">
        <div className="gameplay-area">
          <motion.div
            className={`timer timer-position ${blurQuestion ? "show" : ""}`}
            variants={!blurQuestion ? timerVariants : {}}
            animate={timer < -1 ? "" : timer >= 0 ? "appear" : "disappear"}
          >
            <motion.img
              src="/img/icns/time.png"
              className="time-img"
              alt="time-sand"
              variants={!blurQuestion ? timerVariants : {}}
              animate={timer < 6 && !blurQuestion ? "shakeFaster" : "shake"}
            />
            <span className="timer-text">{blurQuestion ? "30" : timer}</span>

            <AnimatePresence>
              {extraTime > 0 && (
                <motion.div
                  className="extra-time"
                  variants={timerVariants}
                  initial="disappearExtraTime"
                  exit="disappearExtraTime"
                  animate="appearExtraTime"
                >
                  +{extraTime}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          <motion.div
            className="question-area"
            variants={questionVariants}
            initial="hidden"
            animate="visible"
          >
            {prizes}
            <div className={`question ${blurQuestion ? "blur" : ""}`}>
              <TextUnderscore>
                {!blurQuestion
                  ? question
                  : "What is the name of the preserved bodies in ancient Egypt?"}
              </TextUnderscore>
            </div>
          </motion.div>
          <motion.div
            className="answer-area"
            variants={answersVariants}
            initial="hidden"
            animate="visible"
          >
            {children}
          </motion.div>
        </div>
        {/* If is Bonus Game: show .players-area and hide .hint-area */}
        <motion.div
          className="hint-area"
          variants={hintsVariants}
          initial="hidden"
          animate="visible"
        >
          {hint}
        </motion.div>
        <div className="players-area">
          {players.filter(Boolean).map((player, index) => (
            <div key={index} className="player-container">
              {player}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default GameplayLayout;
